import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Phase 16.3.3 — Auth-Store für Closed-Beta-Login.
 *
 * Token + Tester-Info werden in localStorage gepiegelt (per zustand-persist),
 * damit ein Reload nicht zwingend zum Re-Login führt. Bei Mount ruft
 * `AuthGuard` `verifySession()` auf — bei 401 wird ausgeloggt und auf die
 * Login-Page redirectet.
 */
export interface TesterInfo {
  code: string;
  name: string;
}

export interface AuthState {
  token: string | null;
  tester: TesterInfo | null;
  isAuthenticated: boolean;
  /** Letzter verify-Status — `null` bevor je geprüft, sonst Zeitstempel. */
  lastVerifiedAt: number | null;

  login: (code: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  logout: () => Promise<void>;
  verifySession: () => Promise<boolean>;
  /** Setzt den Auth-State direkt — nur für Tests. */
  _setForTest: (next: Partial<AuthState>) => void;
}

const STORAGE_KEY = 'dispatch24_auth';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      tester: null,
      isAuthenticated: false,
      lastVerifiedAt: null,

      login: async (code) => {
        try {
          const res = await fetch('/api/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code }),
          });
          if (!res.ok) {
            const body = (await res.json().catch(() => ({}))) as { error?: string };
            return { ok: false, error: body.error ?? `HTTP ${res.status}` };
          }
          const data = (await res.json()) as { token: string; tester: TesterInfo };
          set({
            token: data.token,
            tester: data.tester,
            isAuthenticated: true,
            lastVerifiedAt: Date.now(),
          });
          return { ok: true };
        } catch (e) {
          return { ok: false, error: `Verbindungsfehler: ${String(e)}` };
        }
      },

      logout: async () => {
        const token = get().token;
        if (token) {
          // Best-effort — bei Fehler trotzdem lokal ausloggen
          try {
            await fetch('/api/auth/logout', {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
            });
          } catch {
            // ignore
          }
        }
        set({ token: null, tester: null, isAuthenticated: false, lastVerifiedAt: null });
      },

      verifySession: async () => {
        const token = get().token;
        if (!token) {
          set({ isAuthenticated: false });
          return false;
        }
        try {
          const res = await fetch('/api/auth/verify', {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.status === 401) {
            // Token ungültig → ausloggen
            set({ token: null, tester: null, isAuthenticated: false, lastVerifiedAt: null });
            return false;
          }
          if (!res.ok) return false;
          const data = (await res.json()) as TesterInfo;
          set({
            tester: data,
            isAuthenticated: true,
            lastVerifiedAt: Date.now(),
          });
          return true;
        } catch {
          // Network-Error → wir lassen den State so wie er ist (Offline-Modus).
          return false;
        }
      },

      _setForTest: (next) => set(next as AuthState),
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({
        token: state.token,
        tester: state.tester,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
